// Yeni WABA şablonu (360dialog). Payload / doğrulama components/CreateDialog360Template ile birebir aynı;
// eski ekran (settings/integrations/dialog360templates) olduğu gibi duruyor.
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import _ from 'lodash';
import { post } from '../../util/api';
import { Icon, Btn, IconBtn, Card, CardHead, PageHead, Field, Input, Select, Check, Segmented, Banner, Spinner, COLORS, notify } from './ui';

const LANGUAGE_OPTIONS = [
	{ value: 'tr', label: 'Turkish (tr)' },
	{ value: 'en', label: 'English (en)' },
	{ value: 'en_US', label: 'English (US)' },
	{ value: 'de', label: 'German (de)' },
	{ value: 'fr', label: 'French (fr)' },
	{ value: 'es', label: 'Spanish (es)' },
	{ value: 'ar', label: 'Arabic (ar)' },
	{ value: 'ru', label: 'Russian (ru)' },
];
const BUTTON_TYPES = [
	{ value: 'QUICK_REPLY', label: 'Quick reply' },
	{ value: 'URL', label: 'URL' },
	{ value: 'PHONE_NUMBER', label: 'Phone number' },
	{ value: 'COPY_CODE', label: 'Copy code (kupon)' },
	{ value: 'CATALOG', label: 'Catalog' },
	{ value: 'MPM', label: 'Multi-product (MPM)' },
	{ value: 'FLOW', label: 'Flow' },
];
const LIMITS = { header: 60, body: 1024, footer: 60 };
const FORMATS = [
	{ label: 'B', marker: '*', title: 'Kalın', style: { fontWeight: 700 } },
	{ label: 'I', marker: '_', title: 'İtalik', style: { fontStyle: 'italic' } },
	{ label: 'S', marker: '~', title: 'Üstü çizili', style: { textDecoration: 'line-through' } },
	{ label: '</>', marker: '```', title: 'Monospace', style: { fontFamily: 'monospace', fontSize: 11 } },
];
const EMOJIS = ['😀', '😉', '😍', '👍', '🙏', '🎉', '🔥', '✅', '❌', '⭐', '❤️', '📦', '🛒', '💰', '⏰', '📢'];
const VAR_RE = /{{\s*([A-Za-z0-9_]+)\s*}}/g;
function extractVars(text) {
	const out = [];
	let m;
	VAR_RE.lastIndex = 0;
	while ((m = VAR_RE.exec(text || ''))) if (!out.includes(m[1])) out.push(m[1]);
	return out;
}
const isNamedToken = (tok) => !/^\d+$/.test(tok);

const defaultState = () => ({
	name: '',
	language: 'tr',
	category: 'MARKETING',
	allow_category_change: false,
	templateKind: 'STANDARD', // STANDARD | CAROUSEL_PRODUCT | CAROUSEL_MEDIA
	components: {
		header: { enabled: false, format: 'TEXT', text: '', url: '', example: '' },
		body: { enabled: true, text: '' },
		footer: { enabled: false, text: '' },
		buttons: [],
		lto: { enabled: false, text: '', has_expiration: false },
		callPermission: { enabled: false },
		variables: {},
		carousel: {
			cardCount: 2,
			buttonText: 'View',
			cardFormat: 'IMAGE',
			cards: [
				{ url: '', body: '', buttons: [] },
				{ url: '', body: '', buttons: [] },
			],
		},
		auth: {
			add_security_recommendation: true,
			code_expiration_minutes: 5,
			otp_type: 'copy_code',
			button_text: '',
			autofill_text: '',
			package_name: '',
			signature_hash: '',
		},
	},
});
function newButton() {
	return { type: 'QUICK_REPLY', text: '', value: '', example: '', flow_id: '', flow_action: 'navigate', navigate_screen: '' };
}
function acceptFor(format) {
	const f = (format || '').toUpperCase();
	if (f === 'VIDEO') return 'video/mp4,video/3gpp';
	if (f === 'DOCUMENT') return 'application/pdf,.pdf';
	return 'image/jpeg,image/png'; // WhatsApp görsel başlığı yalnızca JPEG/PNG
}
function isSvg(url) {
	return /\.svg(\?|#|$)/i.test(url || '');
}

// URL girişi + kalıcı CDN'e yükleme. Modül seviyesinde: her render'da remount olmaz.
function MediaInput({ value, onChange, accept, t }) {
	const [uploading, setUploading] = useState(false);
	async function onFile(e) {
		const input = e.target;
		const file = input.files[0];
		if (!file) return;
		setUploading(true);
		try {
			const fd = new FormData();
			fd.append('files[]', file);
			const resp = await fetch('https://uploads.yapaytech.com/flow/uploadPermImage', { method: 'POST', body: fd });
			const json = await resp.json();
			const url = json && json.files && json.files[0] && json.files[0].url;
			if (url) onChange(url);
			else notify(t('Dosya yüklenemedi.'), 'err');
		} catch (err) {
			notify(t('Dosya yüklenemedi.'), 'err');
		} finally {
			setUploading(false);
			if (input) input.value = '';
		}
	}
	return (
		<div className="wsm-row" style={{ flexWrap: 'nowrap' }}>
			<Input value={value} placeholder="https://…" onChange={onChange} />
			<label className="wsm-iconbtn" style={{ width: 40, height: 40, cursor: 'pointer' }} title={t('Dosya yükle')}>
				{uploading ? <Spinner size={15} /> : <Icon name="upload" size={15} />}
				<input type="file" accept={accept} style={{ display: 'none' }} disabled={uploading} onChange={onFile} />
			</label>
		</div>
	);
}

// Bölüm aç/kapa düğmesi
function Toggle({ on, onClick, icon, children }) {
	return (
		<Btn variant={on ? 'soft' : 'ghost'} size="s" icon={on ? 'check' : icon} onClick={onClick}>
			{children}
		</Btn>
	);
}

export default function TemplateEditor() {
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();
	const params = new URLSearchParams(location.search);
	const waba_gen_id = params.get('waba_gen_id') || '';
	const shop = params.get('shop') || '';

	const [data, setData] = useState(defaultState());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [namedInput, setNamedInput] = useState('');
	const [emojiOpen, setEmojiOpen] = useState(false);
	const bodyRef = useRef(null);

	const isAuth = data.category === 'AUTHENTICATION';
	const kind = isAuth ? 'AUTH' : data.templateKind;
	const setComp = (name, patch) => setData((prev) => ({ ...prev, components: { ...prev.components, [name]: { ...prev.components[name], ...patch } } }));

	const bodyVariables = extractVars(data.components.body.text);
	const hasNamedVar = bodyVariables.some(isNamedToken);
	const hasPositionalVar = bodyVariables.some((v) => !isNamedToken(v));

	function applyToBody(mutate) {
		const ta = bodyRef.current;
		const text = data.components.body.text || '';
		const start = ta ? ta.selectionStart : text.length;
		const end = ta ? ta.selectionEnd : start;
		const res = mutate(text, start, end);
		setComp('body', { text: res.text });
		setTimeout(() => {
			if (!ta) return;
			ta.focus();
			ta.selectionStart = ta.selectionEnd = res.caret;
		}, 0);
	}
	function wrapSelection(marker) {
		applyToBody((text, start, end) => ({ text: text.slice(0, start) + marker + text.slice(start, end) + marker + text.slice(end), caret: end + marker.length * 2 }));
	}
	function insertAtCursor(str) {
		applyToBody((text, start, end) => ({ text: text.slice(0, start) + str + text.slice(end), caret: start + str.length }));
	}
	function addVariable() {
		const nums = bodyVariables.filter((v) => !isNamedToken(v)).map(Number);
		insertAtCursor(`{{${(nums.length ? Math.max(...nums) : 0) + 1}}}`);
	}
	function addNamedVariable() {
		const name = (namedInput || '').trim().replace(/[^A-Za-z0-9_]/g, '_');
		if (!name || /^\d/.test(name)) return;
		insertAtCursor(`{{${name}}}`);
		setNamedInput('');
	}
	function setVariable(key, value) {
		setData((prev) => ({ ...prev, components: { ...prev.components, variables: { ...prev.components.variables, [key]: value } } }));
	}

	// Düz fonksiyon olarak çağrılır (component değil) — input'lar remount olup focus kaybetmesin.
	function renderButtons(buttons, onChange, allowCarousel) {
		const types = allowCarousel ? BUTTON_TYPES.filter((x) => ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'].includes(x.value)) : BUTTON_TYPES;
		const set = (i, patch) => onChange(buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
		return (
			<div className="wsm-col" style={{ gap: 10 }}>
				{buttons.map((b, i) => (
					<div key={i} className="wsm-subcard" style={{ background: '#F8FAFB' }}>
						<div className="wsm-row" style={{ flexWrap: 'nowrap' }}>
							<Select value={b.type} options={types} onChange={(value) => set(i, { type: value, value: '', example: '' })} />
							<IconBtn icon="trash" danger size={40} title={t('delete')} onClick={() => onChange(buttons.filter((x, idx) => idx !== i))} />
						</div>
						{b.type !== 'CATALOG' && b.type !== 'MPM' && b.type !== 'COPY_CODE' ? (
							<Input value={b.text} placeholder={t('Buton başlığı')} maxLength={25} onChange={(v) => set(i, { text: v })} />
						) : null}
						{b.type === 'URL' ? (
							<Field hint={t('Linki gönderimde değiştirmek için sonuna değişken ekleyin, ör.') + ' https://site.com/{{1}}'}>
								<Input value={b.value} placeholder="https://…" onChange={(v) => set(i, { value: v })} />
							</Field>
						) : null}
						{b.type === 'URL' && /{{\d+}}/.test(b.value || '') ? (
							<Field label={t('Örnek URL (değişken için)')} required>
								<Input value={b.example} placeholder="https://site.com/promo" onChange={(v) => set(i, { example: v })} />
							</Field>
						) : null}
						{b.type === 'PHONE_NUMBER' ? <Input type="tel" value={b.value} placeholder="+90…" onChange={(v) => set(i, { value: v })} /> : null}
						{b.type === 'COPY_CODE' ? <Input value={b.value} placeholder={t('Kupon kodu (ör. INDIRIM20)')} onChange={(v) => set(i, { value: v })} /> : null}
						{b.type === 'FLOW' ? (
							<>
								<Field label="Flow ID">
									<Input value={b.flow_id} placeholder="123456789" onChange={(v) => set(i, { flow_id: v })} />
								</Field>
								<div className="wsm-grid2">
									<Field label="Flow action">
										<Select
											value={b.flow_action}
											options={[
												{ value: 'navigate', label: 'navigate' },
												{ value: 'data_exchange', label: 'data_exchange' },
											]}
											onChange={(v) => set(i, { flow_action: v })}
										/>
									</Field>
									{b.flow_action === 'navigate' ? (
										<Field label="Navigate screen">
											<Input value={b.navigate_screen} placeholder="SCREEN_NAME" onChange={(v) => set(i, { navigate_screen: v })} />
										</Field>
									) : null}
								</div>
							</>
						) : null}
					</div>
				))}
				<div>
					<Btn variant="dashed" icon="plus" size="s" disabled={buttons.length >= 10} onClick={() => onChange([...buttons, newButton()])}>
						{t('Buton ekle')}
					</Btn>
				</div>
			</div>
		);
	}

	// Backend buildTemplatePayload ile uyumlu (eski ekranla aynı)
	function buildTemplate() {
		const cmp = data.components;
		const base = { name: (data.name || '').trim().toLowerCase().replace(/\s+/g, '_'), language: data.language, category: data.category, allow_category_change: !!data.allow_category_change };
		if (isAuth) return { ...base, components: { auth: cmp.auth } };
		if (kind === 'CAROUSEL_PRODUCT') {
			return {
				...base,
				components: { body: { text: cmp.body.text }, variables: cmp.variables, carousel: { enabled: true, cardType: 'PRODUCT', cardCount: _.toInteger(cmp.carousel.cardCount) || 2, buttonText: cmp.carousel.buttonText || 'View' } },
			};
		}
		if (kind === 'CAROUSEL_MEDIA') {
			const cards = cmp.carousel.cards.map((cd) => ({ url: cd.url, body: cd.body, buttons: cd.buttons || [] }));
			return { ...base, components: { body: { text: cmp.body.text }, variables: cmp.variables, carousel: { enabled: true, cardType: 'MEDIA', cardFormat: cmp.carousel.cardFormat, cards } } };
		}
		return {
			...base,
			components: {
				header: cmp.header.enabled ? cmp.header : { enabled: false },
				body: { text: cmp.body.text },
				footer: cmp.footer,
				buttons: cmp.buttons,
				variables: cmp.variables,
				lto: data.category === 'MARKETING' && cmp.lto.enabled ? { enabled: true, text: cmp.lto.text, has_expiration: cmp.lto.has_expiration } : { enabled: false },
				callPermission: cmp.callPermission.enabled ? { enabled: true } : { enabled: false },
			},
		};
	}

	function validate() {
		if (!waba_gen_id) return t('WABA hesabı bulunamadı (waba_gen_id).');
		if (!data.name || !data.name.trim()) return t('Şablon adı zorunlu.');
		if (isAuth) {
			const a = data.components.auth;
			if ((a.otp_type === 'one_tap' || a.otp_type === 'zero_tap') && (!a.package_name || !a.signature_hash)) return t('One-tap / Zero-tap için package name ve signature hash zorunlu.');
			return '';
		}
		if (!data.components.body.text || !data.components.body.text.trim()) return t('Gövde metni zorunlu.');
		if (hasNamedVar && hasPositionalVar) return t('İsimli ve numaralı değişkenler aynı şablonda karıştırılamaz.');
		if (bodyVariables.some((v) => !data.components.variables[v])) return t('Değişkenler için örnek değer girin.');
		if (kind === 'STANDARD' && data.category === 'MARKETING' && data.components.lto.enabled && !data.components.lto.text.trim()) return t('Teklif metni zorunlu.');
		if (kind === 'STANDARD' && data.components.header.enabled && data.components.header.format === 'TEXT' && /{{\d+}}/.test(data.components.header.text) && !data.components.header.example)
			return t('Başlık değişkeni için örnek zorunlu.');
		if (kind === 'STANDARD' && data.components.header.enabled && data.components.header.format !== 'TEXT' && isSvg(data.components.header.url)) return t('WhatsApp SVG desteklemez; JPEG/PNG görsel kullanın.');
		if (kind === 'CAROUSEL_MEDIA') {
			const cards = data.components.carousel.cards;
			if (cards.some((c) => !c.url)) return t('Her kart için örnek medya gerekli.');
			if (data.components.carousel.cardFormat === 'IMAGE' && cards.some((c) => isSvg(c.url))) return t('WhatsApp SVG desteklemez; JPEG/PNG görsel kullanın.');
			if (cards.some((c) => (c.buttons || []).some((b) => b.type === 'URL' && /{{\d+}}/.test(b.value || '') && !b.example))) return t('Dinamik URL için örnek zorunlu.');
			const sig = (c) => (c.buttons || []).map((b) => b.type).join(',');
			const first = sig(cards[0]);
			if (cards.some((c) => sig(c) !== first)) return t('Tüm kartlarda aynı sayıda ve aynı tip buton olmalı.');
		} else if (data.components.buttons.some((b) => b.type === 'URL' && /{{\d+}}/.test(b.value || '') && !b.example)) {
			return t('Dinamik URL için örnek zorunlu.');
		}
		return '';
	}

	const backTo = '/wsendmessage/templates' + (shop ? '?shop=' + encodeURIComponent(shop) : '');
	async function submit() {
		const v = validate();
		if (v) {
			setError(v);
			window.scrollTo(0, 0);
			return;
		}
		setError('');
		setLoading(true);
		try {
			const resp = await post('/dahi/channel/dialog360template/create', { waba_gen_id, template: buildTemplate() });
			if (resp && resp.data && resp.data.success) {
				notify(t('Şablon onaya gönderildi') + ': ' + buildTemplate().name);
				history.push(backTo + (shop ? '&' : '?') + 'refresh=1');
				return;
			}
			setError(
				(resp && resp.data && (resp.data.message || _.get(resp.data, 'error.error.error_user_msg') || _.get(resp.data, 'error.message'))) || t('Şablon oluşturulamadı.')
			);
		} catch (e) {
			setError(e.message);
		}
		setLoading(false);
	}

	// ── Form parçaları ──
	function renderBodyField() {
		const body = data.components.body.text || '';
		return (
			<div className="wsm-subcard">
				<div className="wsm-subcard-title">
					<span>{t('Gövde')}</span>
					<div className="wsm-editor-bar" style={{ position: 'relative' }}>
						{FORMATS.map((f) => (
							<button key={f.marker} type="button" className="wsm-iconbtn" title={t(f.title)} style={f.style} onClick={() => wrapSelection(f.marker)}>
								{f.label}
							</button>
						))}
						<IconBtn icon="smile" size={28} title="Emoji" active={emojiOpen} onClick={() => setEmojiOpen(!emojiOpen)} />
						{emojiOpen ? (
							<div className="wsm-emoji-pop" style={{ background: '#fff', border: '1px solid #E2E7EC', borderRadius: 10, padding: 8, display: 'grid', gridTemplateColumns: 'repeat(8, 28px)', gap: 2, boxShadow: '0 12px 32px rgba(15,23,48,.14)' }}>
								{EMOJIS.map((em) => (
									<button
										key={em}
										type="button"
										style={{ border: 0, background: 'transparent', fontSize: 18, cursor: 'pointer', padding: 2 }}
										onClick={() => {
											insertAtCursor(em);
											setEmojiOpen(false);
										}}
									>
										{em}
									</button>
								))}
							</div>
						) : null}
						<Btn variant="soft" size="s" icon="plus" disabled={hasNamedVar} onClick={addVariable} style={{ height: 28 }}>
							{t('Değişken')}
						</Btn>
					</div>
				</div>
				<textarea
					ref={bodyRef}
					className="wsm-textarea"
					rows={5}
					maxLength={LIMITS.body}
					value={body}
					placeholder={t('Gövde metni')}
					onChange={(e) => setComp('body', { text: e.target.value })}
				/>
				<div className="wsm-label-row">
					<div className="wsm-row" style={{ flexWrap: 'nowrap', flex: '1 1 auto' }}>
						<Input
							small
							mono
							value={namedInput}
							disabled={hasPositionalVar}
							placeholder={t('degisken_adi')}
							onChange={setNamedInput}
							onKeyDown={(e) => e.key === 'Enter' && addNamedVariable()}
							style={{ maxWidth: 200 }}
						/>
						<Btn variant="ghost" size="s" disabled={hasPositionalVar || !namedInput.trim()} onClick={addNamedVariable}>
							{t('İsimli değişken ekle')}
						</Btn>
					</div>
					<span className="wsm-hint wsm-nowrap">
						{body.length} / {LIMITS.body}
					</span>
				</div>
				<div className="wsm-hint">{t('İsimli değişken (ör. order_id) ya da numaralı değişken kullanabilirsiniz; ikisi aynı şablonda karıştırılamaz.')}</div>
				{bodyVariables.length ? (
					<div className="wsm-col" style={{ gap: 8 }}>
						<div className="wsm-label">{t('Örnek değerler (Meta onayı için zorunlu)')}</div>
						{bodyVariables.map((vv) => (
							<div key={vv} className="wsm-var" style={{ gridTemplateColumns: 'auto minmax(0, 1fr)' }}>
								<span className="wsm-code">{'{{' + vv + '}}'}</span>
								<Input value={data.components.variables[vv]} placeholder={t('Örnek değer')} onChange={(v) => setVariable(vv, v)} />
							</div>
						))}
					</div>
				) : null}
			</div>
		);
	}

	function renderAuthForm() {
		const a = data.components.auth;
		const setAuth = (patch) => setComp('auth', patch);
		return (
			<div className="wsm-col">
				<Banner tone="info">{t('Doğrulama (OTP) şablonu: gövde metni WhatsApp tarafından sabittir; yalnızca kod butonu ve ayarları girilir.')}</Banner>
				<Check label={t('Güvenlik uyarısı ekle ("Kodu kimseyle paylaşmayın")')} checked={a.add_security_recommendation} onChange={(v) => setAuth({ add_security_recommendation: v })} />
				<div className="wsm-grid2">
					<Field label={t('Kod geçerliliği (dk, 1-90)')}>
						<Input type="number" min={1} max={90} value={a.code_expiration_minutes} onChange={(v) => setAuth({ code_expiration_minutes: v })} />
					</Field>
					<Field label={t('OTP tipi')}>
						<Select
							value={a.otp_type}
							options={[
								{ value: 'copy_code', label: 'Copy code' },
								{ value: 'one_tap', label: 'One tap' },
								{ value: 'zero_tap', label: 'Zero tap' },
							]}
							onChange={(v) => setAuth({ otp_type: v })}
						/>
					</Field>
				</div>
				<Field label={t('Buton metni') + ' (' + t('isteğe bağlı') + ')'}>
					<Input value={a.button_text} onChange={(v) => setAuth({ button_text: v })} />
				</Field>
				{a.otp_type === 'one_tap' || a.otp_type === 'zero_tap' ? (
					<>
						<Field label="Autofill text">
							<Input value={a.autofill_text} onChange={(v) => setAuth({ autofill_text: v })} />
						</Field>
						<div className="wsm-grid2">
							<Field label="Package name" required>
								<Input value={a.package_name} onChange={(v) => setAuth({ package_name: v })} />
							</Field>
							<Field label="Signature hash" required>
								<Input value={a.signature_hash} onChange={(v) => setAuth({ signature_hash: v })} />
							</Field>
						</div>
					</>
				) : null}
			</div>
		);
	}

	function renderStandardForm() {
		const h = data.components.header;
		const foot = data.components.footer;
		const lto = data.components.lto;
		const call = data.components.callPermission;
		const isMarketing = data.category === 'MARKETING';
		return (
			<div className="wsm-col">
				<div className="wsm-row">
					<Toggle on={h.enabled} icon="image" onClick={() => setComp('header', { enabled: !h.enabled })}>
						{t('Başlık')}
					</Toggle>
					<Toggle on={foot.enabled} icon="minus" onClick={() => setComp('footer', { enabled: !foot.enabled })}>
						{t('Alt bilgi')}
					</Toggle>
					{isMarketing ? (
						<Toggle on={lto.enabled} icon="clock" onClick={() => setComp('lto', { enabled: !lto.enabled })}>
							Limited Time Offer
						</Toggle>
					) : null}
					<Toggle on={call.enabled} icon="phone" onClick={() => setComp('callPermission', { enabled: !call.enabled })}>
						{t('Arama izni')}
					</Toggle>
				</div>
				{h.enabled ? (
					<div className="wsm-subcard">
						<div className="wsm-subcard-title">{t('Başlık')}</div>
						<Segmented
							value={h.format}
							onChange={(v) => setComp('header', { format: v })}
							options={[
								{ value: 'TEXT', label: t('Metin') },
								{ value: 'IMAGE', label: t('Görsel'), icon: 'image' },
								{ value: 'VIDEO', label: 'Video', icon: 'play' },
								{ value: 'DOCUMENT', label: t('Belge'), icon: 'file' },
							]}
						/>
						{h.format === 'TEXT' ? (
							<>
								<Field hint={`${(h.text || '').length} / ${LIMITS.header}`}>
									<Input maxLength={LIMITS.header} value={h.text} placeholder={t('Başlık metni')} onChange={(v) => setComp('header', { text: v })} />
								</Field>
								{/{{\d+}}/.test(h.text) ? (
									<Field label={t('Başlık örnek değeri')} required>
										<Input value={h.example} onChange={(v) => setComp('header', { example: v })} />
									</Field>
								) : null}
							</>
						) : (
							<Field label={t('Örnek medya (onay için)')} hint={h.format === 'IMAGE' ? t('Yalnızca JPEG / PNG.') : null}>
								<MediaInput t={t} accept={acceptFor(h.format)} value={h.url} onChange={(url) => setComp('header', { url })} />
							</Field>
						)}
					</div>
				) : null}
				{renderBodyField()}
				{isMarketing && lto.enabled ? (
					<div className="wsm-subcard">
						<div className="wsm-subcard-title">Limited Time Offer</div>
						<div className="wsm-hint">{t('Kampanya / geri sayım teklifi. Kupon kodu (COPY_CODE) ve/veya URL butonu ekleyin. Bitiş zamanı gönderimde verilir.')}</div>
						<Field label={t('Teklif metni')} required>
							<Input maxLength={16} value={lto.text} placeholder={t('%20 indirim')} onChange={(v) => setComp('lto', { text: v })} />
						</Field>
						<Check label={t('Bitiş zamanı göster (geri sayım)')} checked={lto.has_expiration} onChange={(v) => setComp('lto', { has_expiration: v })} />
					</div>
				) : null}
				{call.enabled ? <Banner tone="info">{t('Sesli arama izni isteği eklenir; kullanıcıya arama izni sorulur, ek alan gerekmez.')}</Banner> : null}
				{foot.enabled ? (
					<div className="wsm-subcard">
						<div className="wsm-subcard-title">{t('Alt bilgi')}</div>
						<Field hint={`${(foot.text || '').length} / ${LIMITS.footer}`}>
							<Input maxLength={LIMITS.footer} value={foot.text} onChange={(v) => setComp('footer', { text: v })} />
						</Field>
					</div>
				) : null}
				<div className="wsm-subcard">
					<div className="wsm-subcard-title">
						<span>{t('Butonlar')}</span>
						<span className="wsm-hint">{data.components.buttons.length} / 10</span>
					</div>
					{renderButtons(data.components.buttons, (buttons) => setData((p) => ({ ...p, components: { ...p.components, buttons } })))}
				</div>
			</div>
		);
	}

	function renderProductCarousel() {
		const car = data.components.carousel;
		return (
			<div className="wsm-col">
				<Banner tone="info">{t('Ürünler gönderim anında (product_retailer_id) belirlenir. Onay için 2-10 kart iskeleti oluşturulur.')}</Banner>
				{renderBodyField()}
				<div className="wsm-grid2">
					<Field label={t('Kart sayısı (2-10)')}>
						<Input type="number" min={2} max={10} value={car.cardCount} onChange={(v) => setComp('carousel', { cardCount: v })} />
					</Field>
					<Field label={t('Buton metni')}>
						<Input value={car.buttonText} onChange={(v) => setComp('carousel', { buttonText: v })} />
					</Field>
				</div>
			</div>
		);
	}

	function renderMediaCarousel() {
		const car = data.components.carousel;
		const setCard = (i, patch) => setComp('carousel', { cards: car.cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
		return (
			<div className="wsm-col">
				<Banner tone="info">{t('Her kartın kendi görseli, metni ve butonları olur. Tüm kartlarda aynı medya tipi ve aynı sayıda / tipte buton olmalı. En az 2 kart.')}</Banner>
				{renderBodyField()}
				<div className="wsm-row" style={{ justifyContent: 'space-between' }}>
					<Segmented
						value={car.cardFormat}
						onChange={(v) => setComp('carousel', { cardFormat: v })}
						options={[
							{ value: 'IMAGE', label: t('Görsel'), icon: 'image' },
							{ value: 'VIDEO', label: 'Video', icon: 'play' },
						]}
					/>
					<Btn variant="dashed" icon="plus" size="s" disabled={car.cards.length >= 10} onClick={() => setComp('carousel', { cards: [...car.cards, { url: '', body: '', buttons: [] }] })}>
						{t('Kart ekle')}
					</Btn>
				</div>
				{car.cards.map((cd, i) => (
					<div key={i} className="wsm-subcard">
						<div className="wsm-subcard-title">
							<span>
								{t('Kart')} {i + 1}
							</span>
							{car.cards.length > 2 ? (
								<IconBtn icon="trash" danger title={t('Kartı sil')} onClick={() => setComp('carousel', { cards: car.cards.filter((x, idx) => idx !== i) })} />
							) : null}
						</div>
						<Field label={car.cardFormat === 'VIDEO' ? 'Video' : t('Görsel')} required>
							<MediaInput t={t} accept={acceptFor(car.cardFormat)} value={cd.url} onChange={(url) => setCard(i, { url })} />
						</Field>
						<Field label={t('Kart metni')}>
							<Input value={cd.body} onChange={(v) => setCard(i, { body: v })} />
						</Field>
						<div className="wsm-label">{t('Kart butonları')}</div>
						{renderButtons(cd.buttons || [], (buttons) => setCard(i, { buttons }), true)}
					</div>
				))}
			</div>
		);
	}

	function renderPreview() {
		const { header, body, footer, buttons, carousel, auth, lto, callPermission } = data.components;
		const bodyText = (body.text || '').replace(VAR_RE, (m, tok) => data.components.variables[tok] || m);
		const std = kind === 'STANDARD';
		const headerImg = std && header.enabled && header.format === 'IMAGE' && header.url && !isSvg(header.url) ? header.url : null;
		const headerText = std && header.enabled && header.format === 'TEXT' ? (header.text || '').replace(/{{(\d+)}}/g, (m) => header.example || m) : null;
		const btn = (key, icon, text) => (
			<div key={key} className="wsm-wa-btn">
				{icon ? <Icon name={icon} size={13} /> : null}
				{text}
			</div>
		);
		const btnIcon = (b) => (b.type === 'URL' ? 'external' : b.type === 'PHONE_NUMBER' ? 'phone' : b.type === 'COPY_CODE' ? 'file' : 'reply');
		const strip = (items) => <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginTop: 8, paddingBottom: 4 }}>{items}</div>;
		const cardStyle = { flex: '0 0 150px', background: '#fff', borderRadius: 8, overflow: 'hidden' };
		return (
			<div className="wsm-wa">
				<div className="wsm-wa-title">{t('Canlı önizleme')}</div>
				{isAuth ? (
					<>
						<div className="wsm-wa-bubble">
							{t('<Kod> tek seferlik doğrulama kodunuz.')}
							{auth.add_security_recommendation ? ' ' + t('Bu kodu kimseyle paylaşmayın.') : ''}
							{auth.code_expiration_minutes ? (
								<div className="wsm-wa-foot">
									{t('Geçerlilik')} {auth.code_expiration_minutes} {t('dk')}
								</div>
							) : null}
						</div>
						<div className="wsm-wa-btns">{btn('c', 'file', auth.button_text || t('Kodu kopyala'))}</div>
					</>
				) : (
					<>
						<div className="wsm-wa-bubble">
							{headerImg ? <img alt="" src={headerImg} /> : null}
							{std && header.enabled && header.format !== 'TEXT' && !headerImg ? (
								<div className="wsm-wa-media">
									<Icon name={header.format === 'VIDEO' ? 'play' : header.format === 'DOCUMENT' ? 'file' : 'image'} size={16} color={COLORS.hint} />
								</div>
							) : null}
							{headerText ? <div className="wsm-wa-head">{headerText}</div> : null}
							{std && data.category === 'MARKETING' && lto.enabled ? (
								<div style={{ background: '#FFF2D9', color: COLORS.warn, borderRadius: 6, padding: '4px 8px', fontSize: 12, margin: '2px 0 6px', display: 'flex', justifyContent: 'space-between' }}>
									<span>{lto.text || t('Teklif')}</span>
									{lto.has_expiration ? <span>⏳ 24:00:00</span> : null}
								</div>
							) : null}
							<div>{bodyText || <span className="wsm-faint">{t('Mesaj önizlemesi')}</span>}</div>
							{std && footer.enabled && footer.text ? <div className="wsm-wa-foot">{footer.text}</div> : null}
							<div className="wsm-wa-time">10:00 ✓✓</div>
						</div>
						{std && (buttons.length || callPermission.enabled) ? (
							<div className="wsm-wa-btns">
								{buttons.map((b, i) => btn(i, btnIcon(b), b.text || b.value || b.type))}
								{callPermission.enabled ? btn('call', 'phone', t('Aramaya izin ver')) : null}
							</div>
						) : null}
						{kind === 'CAROUSEL_PRODUCT'
							? strip(
									_.times(_.clamp(_.toInteger(carousel.cardCount) || 2, 2, 10), (i) => (
										<div key={i} style={cardStyle}>
											<div className="wsm-wa-media" style={{ height: 90, margin: 0, borderRadius: 0 }}>
												<Icon name="box" size={18} color={COLORS.hint} />
											</div>
											<div style={{ padding: '4px 8px', fontSize: 12, color: COLORS.muted }}>
												{t('Ürün')} {i + 1}
											</div>
											<div className="wsm-wa-btn" style={{ borderTop: '1px solid #EEF2F5', borderRadius: 0 }}>
												{carousel.buttonText || 'View'}
											</div>
										</div>
									))
							  )
							: null}
						{kind === 'CAROUSEL_MEDIA'
							? strip(
									carousel.cards.map((cd, i) => (
										<div key={i} style={cardStyle}>
											{carousel.cardFormat === 'IMAGE' && cd.url && !isSvg(cd.url) ? (
												<img alt="" src={cd.url} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
											) : (
												<div className="wsm-wa-media" style={{ height: 90, margin: 0, borderRadius: 0 }}>
													<Icon name={carousel.cardFormat === 'VIDEO' ? 'play' : 'image'} size={18} color={COLORS.hint} />
												</div>
											)}
											<div style={{ padding: '6px 8px', fontSize: 12 }}>{cd.body}</div>
											{(cd.buttons || []).map((b, bi) => (
												<div key={bi} className="wsm-wa-btn" style={{ borderTop: '1px solid #EEF2F5', borderRadius: 0 }}>
													{b.text || b.type}
												</div>
											))}
										</div>
									))
							  )
							: null}
					</>
				)}
			</div>
		);
	}

	const checkErr = validate();
	return (
		<div>
			<PageHead
				title={t('Yeni WABA şablonu')}
				desc={(shop ? shop + ' · ' : '') + t('Şablon Meta onayına gönderilir; onaylandıktan sonra Mesaj Gönder sekmesinde seçilebilir.')}
				right={
					<>
						<Btn variant="ghost" icon="chevronLeft" onClick={() => history.push(backTo)} disabled={loading}>
							{t('Şablonlara dön')}
						</Btn>
						<Btn icon="send" busy={loading} onClick={submit}>
							{t('Onaya gönder')}
						</Btn>
					</>
				}
			/>
			{error ? (
				<Banner tone="danger" style={{ marginBottom: 16 }}>
					{error}
				</Banner>
			) : null}
			<div className="wsm-split">
				<div className="wsm-col">
					<Card>
						<CardHead title={t('Temel bilgiler')} />
						<div className="wsm-section wsm-col">
							<Field label={t('Şablon adı')} required hint={t('Küçük harf, rakam ve alt çizgi. Boşluklar alt çizgiye çevrilir.')}>
								<Input mono value={data.name} placeholder="ornek_sablon_adi" maxLength={512} onChange={(v) => setData({ ...data, name: v })} />
							</Field>
							<Field label={t('Kategori')}>
								<Segmented
									value={data.category}
									onChange={(v) => setData({ ...data, category: v })}
									options={[
										{ value: 'MARKETING', label: 'Marketing' },
										{ value: 'UTILITY', label: 'Utility' },
										{ value: 'AUTHENTICATION', label: 'Authentication' },
									]}
								/>
							</Field>
							<div className="wsm-grid2">
								{!isAuth ? (
									<Field label={t('Şablon tipi')}>
										<Select
											value={data.templateKind}
											onChange={(v) => setData({ ...data, templateKind: v })}
											options={[
												{ value: 'STANDARD', label: t('Standart') },
												{ value: 'CAROUSEL_PRODUCT', label: t('Ürün carousel') },
												{ value: 'CAROUSEL_MEDIA', label: t('Medya carousel') },
											]}
										/>
									</Field>
								) : null}
								<Field label={t('Dil')}>
									<Select value={data.language} options={LANGUAGE_OPTIONS} onChange={(v) => setData({ ...data, language: v })} />
								</Field>
							</div>
							{!isAuth ? (
								<Check label={t('Meta kategoriyi değiştirebilsin (allow category change)')} checked={data.allow_category_change} onChange={(v) => setData({ ...data, allow_category_change: v })} />
							) : null}
						</div>
					</Card>
					<Card>
						<CardHead title={t('İçerik')} />
						<div className="wsm-section">
							{isAuth ? renderAuthForm() : kind === 'CAROUSEL_PRODUCT' ? renderProductCarousel() : kind === 'CAROUSEL_MEDIA' ? renderMediaCarousel() : renderStandardForm()}
						</div>
						<div className="wsm-stepnav">
							<span className="wsm-stepnav-info" style={checkErr ? { color: COLORS.warn } : undefined}>
								{checkErr || t('Şablon onaya gönderilmeye hazır.')}
							</span>
							<Btn icon="send" busy={loading} onClick={submit}>
								{t('Onaya gönder')}
							</Btn>
						</div>
					</Card>
				</div>
				<Card className="wsm-aside">
					<div className="wsm-aside-head">
						<h3 className="wsm-card-title">{t('Önizleme')}</h3>
					</div>
					<div className="wsm-aside-body">{renderPreview()}</div>
				</Card>
			</div>
		</div>
	);
}
